import React from 'react';
import { useTranslation } from 'react-i18next';
import { sensorRating, getPillStatusFromRating } from 'commons/src/commonFunctions';
import StatusPill from 'commons/src/components/pills/StatusPill';
import { SensorStatusWithIcon } from 'commons/src/components/sensors/SensorStatusDot';
import { sensorUnits } from 'commons/src/constants';
import { Rating, SensorTypes, PillStatus } from 'commons/src/models/commonEnums';
import { ReportSectionWrapper } from '../../../components/wrappers';
import { IaqInsightData, ReportSensorThresholds } from '../../../models/common';
import styles from './IAQInsight2SensorMetrics.module.scss';

export type IAQInsight2SensorMetricsProps = {
    sensorReports: { [sensor: string]: IaqInsightData };
    thresholds: { [sensor: string]: ReportSensorThresholds };
};

type Props = {
    sensor: SensorTypes;
    sensorReports: { [sensor: string]: IaqInsightData };
    thresholds: { [sensor: string]: ReportSensorThresholds };
};

export const SensorAverageComponent = (props: Props): React.ReactElement => {
    const { sensor, sensorReports, thresholds } = props;
    const { t: txt } = useTranslation();

    const sensorDetails: IaqInsightData = sensorReports[sensor];
    const value = sensorDetails.averageValueWithinOpeningHours;
    const sensorThreshold = thresholds[sensor];
    const sensorHasValue = value !== undefined && value !== null && !Number.isNaN(value);
    const valueRange: Rating | undefined = sensorRating(sensorThreshold.ranges, value);
    const displayedUnit = sensorUnits[sensorThreshold.unit];
    const displayedValue = value && (displayedUnit === sensorUnits.pci ? value.toFixed(1) : value.toFixed(0));

    return (
        <div key={`${sensor}-average`} className={styles.sensorContainer}>
            <SensorStatusWithIcon sensor={sensor} />
            <div>
                <div className={styles.sensorInfo}>
                    <span className={styles.sensorLabel}>{txt(sensor)}</span>
                    {getPillStatusFromRating(valueRange) && (
                        <StatusPill
                            status={getPillStatusFromRating(valueRange) || PillStatus.success}
                            outline
                            size="small"
                        />
                    )}
                </div>
                <span className={styles.sensorValue}>
                    {sensorHasValue ? displayedValue : txt('NotAvailableAbbreviation')}
                    <span className={styles.sensorUnit}>{displayedUnit}</span>
                </span>
            </div>
        </div>
    );
};

const IAQInsight2SensorMetrics: React.FC<IAQInsight2SensorMetricsProps> = ({ sensorReports, thresholds }) => {
    const { t: txt } = useTranslation();
    const sensorReportArray = Object.keys(sensorReports);
    return (
        <ReportSectionWrapper>
            <h3 className={styles.sectionTitle}>{txt('InsightIaq.AverageMeasurementOpeningHours')}</h3>
            <div className={styles.sensorGrid}>
                {sensorReportArray
                    .sort((sensorA, sensorB) => sensorA.localeCompare(sensorB))
                    .map(sensor => (
                        <SensorAverageComponent
                            key={`building-${sensor}-average`}
                            sensor={sensor as SensorTypes}
                            sensorReports={sensorReports}
                            thresholds={thresholds}
                        />
                    ))}
            </div>
            <p className={styles.sectionDescription}>{txt('InsightIaq.MeasurementResultExplained')}</p>
        </ReportSectionWrapper>
    );
};

export default IAQInsight2SensorMetrics;
